'use client'

import { useEffect, useState } from 'react'
import { CheckCircleIcon, XCircleIcon, InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline'

type ToastType = 'success' | 'error' | 'info'

interface ToastItem {
    id: number
    type: ToastType
    message: string
}

interface ToastOptions {
    duration?: number
}

const DEFAULT_DURATION = 5000

let nextId = 0
const subscribers = new Set<(t: ToastItem, duration: number) => void>()

function push(type: ToastType, message: string, options?: ToastOptions) {
    const item: ToastItem = { id: ++nextId, type, message }
    const duration = options?.duration ?? DEFAULT_DURATION
    subscribers.forEach(fn => fn(item, duration))
}

export const toast = {
    success: (message: string, options?: ToastOptions) => push('success', message, options),
    error: (message: string, options?: ToastOptions) => push('error', message, options),
    info: (message: string, options?: ToastOptions) => push('info', message, options),
}

const iconWrapClass: Record<ToastType, string> = {
    success: 'text-green-500 bg-green-100 dark:text-green-200 dark:bg-green-800',
    error: 'text-red-500 bg-red-100 dark:text-red-200 dark:bg-red-800',
    info: 'text-blue-500 bg-blue-100 dark:text-blue-200 dark:bg-blue-800',
}

const iconFor: Record<ToastType, typeof CheckCircleIcon> = {
    success: CheckCircleIcon,
    error: XCircleIcon,
    info: InformationCircleIcon,
}

export function Toaster() {
    const [items, setItems] = useState<ToastItem[]>([])

    useEffect(() => {
        const fn = (item: ToastItem, duration: number) => {
            setItems(prev => [...prev, item])
            setTimeout(() => {
                setItems(prev => prev.filter(x => x.id !== item.id))
            }, duration)
        }
        subscribers.add(fn)
        return () => { subscribers.delete(fn) }
    }, [])

    const dismiss = (id: number) => setItems(prev => prev.filter(x => x.id !== id))

    return (
        <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none max-w-full">
            {items.map(item => {
                const Icon = iconFor[item.type]
                return (
                    <div
                        key={item.id}
                        role="alert"
                        className="pointer-events-auto flex items-start w-full max-w-sm p-4 text-gray-500 bg-white rounded-lg shadow-lg dark:text-gray-400 dark:bg-gray-800 ring-1 ring-black/5 dark:ring-white/10"
                    >
                        <div className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${iconWrapClass[item.type]}`}>
                            <Icon className="h-5 w-5" />
                        </div>
                        <div className="ms-3 me-2 text-sm font-normal break-words">{item.message}</div>
                        <button
                            type="button"
                            aria-label="Close"
                            onClick={() => dismiss(item.id)}
                            className="ms-auto -mx-1.5 -my-1.5 bg-white text-gray-400 hover:text-gray-900 rounded-lg focus:ring-2 focus:ring-gray-300 p-1.5 hover:bg-gray-100 inline-flex items-center justify-center h-8 w-8 dark:text-gray-500 dark:hover:text-white dark:bg-gray-800 dark:hover:bg-gray-700"
                        >
                            <XMarkIcon className="h-5 w-5" />
                        </button>
                    </div>
                )
            })}
        </div>
    )
}
